import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Alert,
	Button,
	Box,
	Chip,
	Collapse,
	FormControl,
	Grid,
	IconButton,
	InputLabel,
	CircularProgress,
	Checkbox,
	ListItemText,
	MenuItem,
	Pagination,
	Select,
	TextField,
	Typography,
} from "@mui/material";

import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { saveAs } from "file-saver";

import { SubHeader } from "../../utils/componentes-utiles/SubHeader";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import {
	actualizarCredencialesUsuario,
	eliminarDocumentoPersona,
	descargarDocumentoPersona,
	getCarpetasDocumentacion,
	getDocumentosPersona,
	getListadoDocumentosPersona,
	getRolesYDepartamentos,
	getUsuariosMovil,
	sincronizar,
	subirDocumentosPersona,
} from "../../services/apis";
import { createPersonasDocumentacionExcelWorkbook } from "../../utils/exportPersonasDocumentacionExcel";
import { formatDateTimeEs, formatScoreOverHundred } from "../../utils/formatters";
import { AvatarPersona } from "./AvatarPersona";
import { CredencialesUsuarioDialog } from "./CredencialesUsuarioDialog";
import { FichajePreviewDialog } from "./FichajePreviewDialog";
import { PersonaCard } from "./PersonaCard";
import { SincronizarTrabajadoresDialog } from "./SincronizarTrabajadoresDialog";

const getPersonaDocumentFallbackName = (documento) =>
	documento?.archivo_nombre || documento?.nombre || `documento-${documento?.id ?? "persona"}`;

const normalizeText = (value) =>
	String(value || "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.trim();

const getPersonaDisplayName = (persona) =>
	[persona?.nombre, persona?.apellidos].filter(Boolean).join(" ").trim() ||
	persona?.nombre_completo ||
	`Persona ${persona?.id ?? "-"}`;

const getDocumentName = (documento) =>
	documento?.nombre || documento?.archivo_nombre || `Documento ${documento?.id ?? "-"}`;

const EXPIRY_WARNING_DAYS = 15;
const WORK_SOLUTIONS_ANDROID_URL =
	"https://play.google.com/store/apps/details?id=com.worksolutions.app";
const WORK_SOLUTIONS_IOS_URL =
	"https://apps.apple.com/es/app/work-solutions/id6778411836";

const toIsoDate = (value) => String(value || "").trim().slice(0, 10);

const getExpiryDiffDays = (value) => {
	const normalized = toIsoDate(value);

	if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
		return null;
	}

	const [year, month, day] = normalized.split("-").map(Number);
	const expiryUtc = Date.UTC(year, month - 1, day);
	const today = new Date();
	const todayUtc = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());

	return Math.floor((expiryUtc - todayUtc) / (1000 * 60 * 60 * 24));
};

const hasRedDocumentStatus = (value) =>
	[
		"pendiente",
		"revision",
		"en revision",
		"en_revision",
		"caducado",
		"rechazado",
		"error",
		"inactivo",
	].includes(normalizeText(value));

const matchesDateRange = (value, from, to) => {
	const normalizedValue = toIsoDate(value);

	if (!normalizedValue) {
		return false;
	}

	if (from && normalizedValue < from) {
		return false;
	}

	if (to && normalizedValue > to) {
		return false;
	}

	return true;
};

const documentMatchesDateRange = (documento, from, to) => {
	if (!from && !to) {
		return true;
	}

	return [documento?.fecha_creacion, documento?.fecha_caducidad].some((value) =>
		matchesDateRange(value, from, to)
	);
};

const documentMatchesText = (documento, value) => {
	const normalizedQuery = normalizeText(value);

	if (!normalizedQuery) {
		return true;
	}

	return [
		getDocumentName(documento),
		documento?.archivo_nombre,
		documento?.tipo,
		documento?.extension,
		documento?.campana_nombre,
		documento?.carpeta_nombre,
		documento?.fecha_caducidad,
		documento?.fecha_creacion,
	]
		.some((candidate) => normalizeText(candidate).includes(normalizedQuery));
};

const getDocumentoCategoriaEstado = (documento) => {
	const folderId = Number(documento?.id_carpeta_documentacion || 0);
	const isFolderActive = documento?.carpeta_activa !== false;
	const isFolderDeleted = documento?.carpeta_eliminada === true;
	const normalizedEstado = normalizeText(documento?.estado);

	if (folderId <= 0 || !isFolderActive || isFolderDeleted) {
		return "historico";
	}

	if (hasRedDocumentStatus(normalizedEstado)) {
		return "caducado";
	}

	const diffDays = getExpiryDiffDays(documento?.fecha_caducidad);
	if (diffDays !== null) {
		if (diffDays < 0) {
			return "caducado";
		}

		if (diffDays <= EXPIRY_WARNING_DAYS) {
			return "proximo";
		}
	}

	return "vigente";
};

const getEstadoFiltroLabel = (value) => {
	switch (value) {
		case "vigente":
			return "Vigente";
		case "proximo":
			return "Caduca pronto";
		case "pendiente":
			return "Pendiente";
		case "caducado":
			return "Caducado";
		case "historico":
			return "Historico";
		default:
			return "Todos";
	}
};

const boolToSiNo = (value) => (value ? "Si" : "No");
const boolToSiNoAccent = (value) => (value ? "Sí" : "No");

const getPersonaCompanyNames = (persona, fallbackFolderLabel = "") => {
	const companyNames = Array.from(
		new Set(
			(Array.isArray(persona?.historial_fichajes) ? persona.historial_fichajes : [])
				.map((item) => String(item?.nombre_cliente || "").trim())
				.filter(Boolean)
		)
	);

	if (companyNames.length > 0) {
		return companyNames.map((name) => `*${name}`).join("\n");
	}

	return fallbackFolderLabel || "";
};

const getLatestDocumentByMatchers = (personaDocuments, matchers = []) => {
	const normalizedMatchers = matchers.map((matcher) => normalizeText(matcher));

	return [...(Array.isArray(personaDocuments) ? personaDocuments : [])]
		.filter((documento) => {
			const haystack = normalizeText(
				[
					documento?.nombre,
					documento?.archivo_nombre,
					documento?.tipo,
					documento?.campana_nombre,
					documento?.carpeta_nombre,
				]
					.filter(Boolean)
					.join(" ")
			);

			return normalizedMatchers.some((matcher) => haystack.includes(matcher));
		})
		.sort((left, right) =>
			String(right?.fecha_creacion || right?.fecha_caducidad || "").localeCompare(
				String(left?.fecha_creacion || left?.fecha_caducidad || "")
			)
		)[0];
};

const getAuroraDocumentStatus = (personaDocuments, matchers = []) => {
	const matchingDocument = getLatestDocumentByMatchers(personaDocuments, matchers);

	if (!matchingDocument) {
		return "Pendiente de subir";
	}

	const categoria = getDocumentoCategoriaEstado(matchingDocument);

	if (categoria === "vigente" || categoria === "proximo") {
		return "Subido";
	}

	if (categoria === "caducado") {
		return "Caducado";
	}

	if (categoria === "historico") {
		return "Historico";
	}

	return getEstadoFiltroLabel(categoria);
};

const fetchUsuariosMovilListado = async ({
	page = 1,
	limit = 25,
	search = "",
	rol = "",
	foto = "todas",
	activo = "todos",
	credenciales = "todos",
	documento = "",
	carpeta = [],
	delegacion = [],
	estadoDocumento = "",
	fechaDocumentoDesde = "",
	fechaDocumentoHasta = "",
	historial = true,
	historialLimit = 5,
	all = false,
} = {}) => {
	const response = await getUsuariosMovil({
		page,
		limit,
		search,
		rol,
		foto,
		activo,
		credenciales,
		documento,
		carpeta,
		delegacion,
		estado_documento: estadoDocumento,
		fecha_documento_desde: fechaDocumentoDesde,
		fecha_documento_hasta: fechaDocumentoHasta,
		all,
		historial,
		historialLimit,
	});

	if (response?.success !== true) {
		throw new Error(response?.message || "No se pudieron cargar los usuarios");
	}

	return {
		usuarios: Array.isArray(response?.data?.usuarios) ? response.data.usuarios : [],
		delegaciones: Array.isArray(response?.data?.delegaciones)
			? response.data.delegaciones
			: [],
		pagination: response?.data?.pagination || {
			page,
			limit,
			total: 0,
			total_pages: 1,
			has_more: false,
			all,
		},
	};
};

const hasPasswordConfigured = (persona) =>
	persona?.tiene_contrasena === true || Number(persona?.tiene_contrasena || 0) === 1;

const hasEmailConfigured = (persona) =>
	typeof persona?.mail === "string" && persona.mail.trim() !== "";

const hasCredencialesActivas = (persona) =>
	Number(persona?.credenciales_activos || 0) === 1 &&
	hasPasswordConfigured(persona) &&
	hasEmailConfigured(persona);

const getCredentialSafeText = (value) =>
	normalizeText(value).replace(/[^a-z0-9]/g, "");

const buildCredentialSuggestions = (persona) => {
	const firstName = getCredentialSafeText(String(persona?.nombre || "").split(/\s+/)[0]);
	const lastName = getCredentialSafeText(String(persona?.apellidos || "").split(/\s+/)[0]);
	const nifPassword = String(persona?.nif || "").trim();
	const emailUser = firstName || lastName || "usuario";

	return {
		mail: `${emailUser}@planaett.com`,
		password: nifPassword,
	};
};

const normalizeWhatsappPhone = (value) => {
	let digits = String(value || "").replace(/\D/g, "");

	if (digits.startsWith("00")) {
		digits = digits.slice(2);
	}

	if (digits.length === 9) {
		digits = `34${digits}`;
	}

	return digits;
};

const buildWhatsappCredentialsMessage = ({ persona, mail, password }) =>
	[
		`Hola ${getPersonaDisplayName(persona)},`,
		"",
		"Estos son tus credenciales para Work Solutions:",
		`Correo: ${mail}`,
		`Contrasena: ${password}`,
		"",
		"La app la puedes descargar en:",
		`Android: ${WORK_SOLUTIONS_ANDROID_URL}`,
		`iPhone: ${WORK_SOLUTIONS_IOS_URL}`,
	].join("\n");

export const Personas = () => {
	const [openRow, setOpenRow] = useState(null);
	const [personas, setPersonas] = useState([]);
	const [carpetasDocumentacion, setCarpetasDocumentacion] = useState([]);
	const [delegacionesDisponibles, setDelegacionesDisponibles] = useState([]);
	const [rolesDisponibles, setRolesDisponibles] = useState([]);
	const [loaded, setLoaded] = useState(false);
	const [error, setError] = useState("");
	const [documentacionWarning, setDocumentacionWarning] = useState("");
	const [busqueda, setBusqueda] = useState("");
	const [filtroRol, setFiltroRol] = useState("");
	const [filtroDocumento, setFiltroDocumento] = useState("");
	const [filtroCarpeta, setFiltroCarpeta] = useState([]);
	const [filtroDelegacion, setFiltroDelegacion] = useState([]);
	const [filtroEstadoDocumento, setFiltroEstadoDocumento] = useState("");
	const [fechaDocumentoDesde, setFechaDocumentoDesde] = useState("");
	const [fechaDocumentoHasta, setFechaDocumentoHasta] = useState("");
	const [filtroFoto, setFiltroFoto] = useState("todas");
	const [filtroActivo, setFiltroActivo] = useState("todos");
	const [filtroCredenciales, setFiltroCredenciales] = useState("todos");
	const [openFiltrosDocumentales, setOpenFiltrosDocumentales] = useState(true);
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(25);
	const [totalPages, setTotalPages] = useState(1);
	const [totalResults, setTotalResults] = useState(0);
	const [syncing, setSyncing] = useState(false);
	const [syncMsg, setSyncMsg] = useState("");
	const [openSyncDialog, setOpenSyncDialog] = useState(false);
	const [syncMode, setSyncMode] = useState("hoy");
	const [exporting, setExporting] = useState(false);
	const [fichajePreview, setFichajePreview] = useState(null);
	const [credentialsDialogPersona, setCredentialsDialogPersona] = useState(null);
	const [credentialsEnabled, setCredentialsEnabled] = useState(true);
	const [credentialsMail, setCredentialsMail] = useState("");
	const [credentialsPhone, setCredentialsPhone] = useState("");
	const [credentialsPassword, setCredentialsPassword] = useState("");
	const [credentialsPasswordConfirm, setCredentialsPasswordConfirm] = useState("");
	const [credentialsPasswordTouched, setCredentialsPasswordTouched] = useState(false);
	const [credentialsDialogError, setCredentialsDialogError] = useState("");
	const [credentialsSaving, setCredentialsSaving] = useState(false);
	const loadRequestRef = useRef(0);

	const navigate = useNavigate();
	const location = useLocation();
	const isActive = (path) => location.pathname === path;

	const debouncedSearch = useDebouncedValue(busqueda, 350);

	const hoyYMD = useMemo(() => {
		const d = new Date();
		return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
			d.getDate()
		).padStart(2, "0")}`;
	}, []);

	const [desde, setDesde] = useState(hoyYMD);
	const [hasta, setHasta] = useState(hoyYMD);

	const cargarCarpetasFiltro = useCallback(async () => {
		try {
			const response = await getCarpetasDocumentacion();

			if (response?.success !== true) {
				setCarpetasDocumentacion([]);
				setDocumentacionWarning(
					response?.message || "No se pudieron cargar las carpetas."
				);
				return;
			}

			setCarpetasDocumentacion(
				Array.isArray(response?.data?.carpetas) ? response.data.carpetas : []
			);
			setDocumentacionWarning("");
		} catch (folderError) {
			console.error("No se pudieron cargar las carpetas documentales:", folderError);
			setCarpetasDocumentacion([]);
			setDocumentacionWarning("No se pudieron cargar las carpetas.");
		}
	}, []);

	const cargarRolesFiltro = useCallback(async () => {
		try {
			const response = await getRolesYDepartamentos();

			if (response?.success !== true) {
				setRolesDisponibles([]);
				return;
			}

			setRolesDisponibles(Array.isArray(response?.data?.roles) ? response.data.roles : []);
		} catch (rolesError) {
			console.error("No se pudieron cargar los roles:", rolesError);
			setRolesDisponibles([]);
		}
	}, []);

	const cargarContextoPersonas = useCallback(async ({ showLoader = true } = {}) => {
		const currentRequest = ++loadRequestRef.current;
		if (showLoader) {
			setLoaded(false);
		}
		setError("");

		try {
			const usuariosResult = await fetchUsuariosMovilListado({
				page,
				limit,
				search: debouncedSearch,
				rol: filtroRol,
				foto: filtroFoto,
				activo: filtroActivo,
				credenciales: filtroCredenciales,
				documento: filtroDocumento,
				carpeta: filtroCarpeta,
				delegacion: filtroDelegacion,
				estadoDocumento: filtroEstadoDocumento,
				fechaDocumentoDesde,
				fechaDocumentoHasta,
				historial: true,
				historialLimit: 5,
				all: false,
			});

			if (currentRequest !== loadRequestRef.current) {
				return;
			}

			const pagination = usuariosResult?.pagination || {};
			const nextTotalPages = Math.max(
				1,
				Number(pagination?.total_pages || 1)
			);

			setPersonas(
				Array.isArray(usuariosResult?.usuarios) ? usuariosResult.usuarios : []
			);
			setTotalResults(Number(pagination?.total || 0));
			setTotalPages(nextTotalPages);
			setDelegacionesDisponibles(
				Array.isArray(usuariosResult?.delegaciones)
					? usuariosResult.delegaciones
					: []
			);
		} catch (e) {
			console.error(e);

			if (currentRequest !== loadRequestRef.current) {
				return;
			}

			setPersonas([]);
			setTotalResults(0);
			setTotalPages(1);
			setError(e?.message || "Error de red al cargar usuarios");
		} finally {
			if (currentRequest === loadRequestRef.current) {
				setLoaded(true);
			}
		}
	}, [
		debouncedSearch,
		filtroActivo,
		filtroCarpeta,
		filtroDelegacion,
		filtroCredenciales,
		filtroDocumento,
		filtroEstadoDocumento,
		filtroFoto,
		filtroRol,
		fechaDocumentoDesde,
		fechaDocumentoHasta,
		limit,
		page,
	]);

	const refrescarResumenDocumental = useCallback(async () => {
		try {
			await cargarContextoPersonas({ showLoader: false });
		} catch (refreshError) {
			console.error("No se pudo refrescar el resumen documental:", refreshError);
		}
	}, [cargarContextoPersonas]);

	const handleAvatarUploaded = (updatedPersona) => {
		setPersonas((currentPersonas) =>
			currentPersonas.map((persona) =>
				persona.id === updatedPersona.id
					? { ...persona, imagen_url: updatedPersona.imagen_url }
					: persona
			)
		);
	};

	const resetListadoPage = useCallback(() => {
		setPage(1);
		setOpenRow(null);
	}, []);

	useEffect(() => {
		void cargarContextoPersonas();
	}, [cargarContextoPersonas]);

	useEffect(() => {
		void cargarCarpetasFiltro();
	}, [cargarCarpetasFiltro]);

	useEffect(() => {
		void cargarRolesFiltro();
	}, [cargarRolesFiltro]);

	const carpetasFiltroDisponibles = useMemo(() => {
		return carpetasDocumentacion
			.map((folder) => {
				const folderId = Number(folder?.id || 0);

				if (folderId <= 0) {
					return null;
				}

				return {
					id: folderId,
					nombre: String(folder?.nombre || `Carpeta ${folderId}`),
					campana_nombre: String(folder?.campana_nombre || "Sin campana"),
					etiqueta: `${String(folder?.campana_nombre || "Sin campana")} · ${String(
						folder?.nombre || `Carpeta ${folderId}`
					)}`,
					activa: folder?.activa === true,
					historica: folder?.activa !== true,
				};
			})
			.filter(Boolean)
			.sort((left, right) => {
				if (left.activa !== right.activa) {
					return left.activa ? -1 : 1;
				}

				return left.etiqueta.localeCompare(right.etiqueta, "es");
			});
	}, [carpetasDocumentacion]);

	const carpetasFiltroById = useMemo(
		() =>
			new Map(
				carpetasFiltroDisponibles.map((folder) => [Number(folder.id), folder])
			),
		[carpetasFiltroDisponibles]
	);

	const hasDocumentFilters = Boolean(
		filtroDocumento ||
		filtroCarpeta.length > 0 ||
		filtroDelegacion.length > 0 ||
		filtroEstadoDocumento ||
		fechaDocumentoDesde ||
		fechaDocumentoHasta
	);

	const activeDocumentFiltersCount = [
		filtroDocumento,
		filtroCarpeta.length > 0,
		filtroDelegacion.length > 0,
		filtroEstadoDocumento,
		fechaDocumentoDesde,
		fechaDocumentoHasta,
	].filter(Boolean).length;

	useEffect(() => {
		if (page > totalPages) {
			setPage(totalPages);
		}
	}, [page, totalPages]);

	useEffect(() => {
		if (hasDocumentFilters) {
			setOpenFiltrosDocumentales(true);
		}
	}, [hasDocumentFilters]);

	const limpiarFiltrosDocumentales = () => {
		resetListadoPage();
		setFiltroDocumento("");
		setFiltroCarpeta([]);
		setFiltroDelegacion([]);
		setFiltroEstadoDocumento("");
		setFechaDocumentoDesde("");
		setFechaDocumentoHasta("");
	};

	const exportarPersonasFiltradas = useCallback(async () => {
		if (totalResults === 0 || exporting) {
			return;
		}

		setExporting(true);
		setSyncMsg("");

		try {
			const [usuariosResult, documentosResult] = await Promise.allSettled([
				fetchUsuariosMovilListado({
					page: 1,
					limit: 50,
					search: debouncedSearch,
					rol: filtroRol,
					foto: filtroFoto,
					activo: filtroActivo,
					credenciales: filtroCredenciales,
					documento: filtroDocumento,
					carpeta: filtroCarpeta,
					delegacion: filtroDelegacion,
					estadoDocumento: filtroEstadoDocumento,
					fechaDocumentoDesde,
					fechaDocumentoHasta,
					historial: true,
					historialLimit: 5,
					all: true,
				}),
				getListadoDocumentosPersona(),
			]);

			if (usuariosResult.status !== "fulfilled") {
				throw usuariosResult.reason;
			}

			const usuariosExportados = Array.isArray(usuariosResult.value?.usuarios)
				? usuariosResult.value.usuarios
				: [];
			const allDocuments =
				documentosResult.status === "fulfilled" &&
				documentosResult.value?.success === true &&
				Array.isArray(documentosResult.value?.data?.documentos)
					? documentosResult.value.data.documentos
					: [];
			const documentsByPersona = new Map();

			allDocuments.forEach((documento) => {
				const personaId = Number(documento?.id_usuario || 0);

				if (personaId <= 0) {
					return;
				}

				const currentDocuments = documentsByPersona.get(personaId) || [];
				currentDocuments.push(documento);
				documentsByPersona.set(personaId, currentDocuments);
			});

			const normalizedDocumentoSearch = normalizeText(filtroDocumento);
			const selectedFolderIds = new Set(
				filtroCarpeta.map((folderId) => Number(folderId)).filter((folderId) => folderId > 0)
			);
			const selectedFolders = filtroCarpeta
				.map((folderId) => carpetasFiltroById.get(Number(folderId)))
				.filter(Boolean);
			const selectedFolderLabel = selectedFolders.map((folder) => folder.etiqueta).join(" | ");
			const selectedFolderContext = selectedFolders[0] || null;
			const hasDateFilter = Boolean(fechaDocumentoDesde || fechaDocumentoHasta);
			const resolveMatchingDocuments = (personaDocuments) => {
				if (!hasDocumentFilters) {
					return personaDocuments;
				}

				const documentosBase = personaDocuments.filter((documento) => {
					if (
						selectedFolderIds.size > 0 &&
						!selectedFolderIds.has(Number(documento?.id_carpeta_documentacion || 0))
					) {
						return false;
					}

					if (
						normalizedDocumentoSearch !== "" &&
						!documentMatchesText(documento, normalizedDocumentoSearch)
					) {
						return false;
					}

					if (
						hasDateFilter &&
						!documentMatchesDateRange(
							documento,
							fechaDocumentoDesde,
							fechaDocumentoHasta
						)
					) {
						return false;
					}

					return true;
				});

				if (filtroEstadoDocumento === "pendiente") {
					return [];
				}

				return filtroEstadoDocumento
					? documentosBase.filter(
							(documento) =>
								getDocumentoCategoriaEstado(documento) === filtroEstadoDocumento
					  )
					: documentosBase;
			};

			const personasRows = usuariosExportados.map((persona) => {
					const personaDocuments =
						documentsByPersona.get(Number(persona?.id || 0)) || [];
					const resumen = persona?.documentacion_resumen || {};
					const documentosExportables = resolveMatchingDocuments(personaDocuments);
					const pendienteCoincidente = filtroEstadoDocumento === "pendiente";

					const latestDocument = [...documentosExportables].sort((left, right) =>
						String(right?.fecha_creacion || "").localeCompare(
							String(left?.fecha_creacion || "")
						)
					)[0];

					return {
						Persona: getPersonaDisplayName(persona),
						NIF: persona?.nif || "",
						Departamento: persona?.nombre_departamento || "",
						Rol: persona?.nombre_rol || "",
						Activo: boolToSiNo(Number(persona?.activo || 0) === 1),
						Credenciales: boolToSiNo(hasCredencialesActivas(persona)),
						Email: persona?.mail || "",
						Vigentes: Number(resumen?.vigentes || 0),
						"Caducan pronto": Number(resumen?.proximos || 0),
						"En rojo": Number(
							resumen?.rojos ||
								Number(resumen?.pendientes || 0) + Number(resumen?.caducados || 0)
						),
						Carpeta: selectedFolderLabel,
						Estado: getEstadoFiltroLabel(filtroEstadoDocumento),
						"Documento(s) coincidente(s)": pendienteCoincidente
							? "Pendiente de subir"
							: documentosExportables
									.map((documento) => getDocumentName(documento))
									.join(" | "),
						"Fecha documento": latestDocument
							? formatDateTimeEs(
									latestDocument?.fecha_creacion || latestDocument?.fecha_caducidad,
									{ includeTime: false }
							  )
							: "",
						"Caducidad proxima": latestDocument?.fecha_caducidad
							? formatDateTimeEs(latestDocument.fecha_caducidad, { includeTime: false })
							: "",
					};
				});

			const documentosRows = usuariosExportados.flatMap((persona) => {
					const personaDocuments =
						documentsByPersona.get(Number(persona?.id || 0)) || [];
					const documentosExportables = resolveMatchingDocuments(personaDocuments);
					const pendienteCoincidente = filtroEstadoDocumento === "pendiente";

					if (pendienteCoincidente && documentosExportables.length === 0) {
						return [
							{
								Persona: getPersonaDisplayName(persona),
								NIF: persona?.nif || "",
								Carpeta: selectedFolderLabel || "Pendiente",
								Documento: "Pendiente de subir",
								Estado: "pendiente",
								"Fecha subida": "",
								Caducidad: "",
								Formato: "",
								Tipo: "",
							},
						];
					}

					return documentosExportables.map((documento) => ({
						Persona: getPersonaDisplayName(persona),
						NIF: persona?.nif || "",
						Carpeta: `${documento?.campana_nombre || "Sin campana"} · ${
							documento?.carpeta_nombre || "Sin carpeta"
						}`,
						Documento: getDocumentName(documento),
						Estado: getDocumentoCategoriaEstado(documento),
						"Fecha subida": documento?.fecha_creacion
							? formatDateTimeEs(documento.fecha_creacion, { includeTime: false })
							: "",
						Caducidad: documento?.fecha_caducidad
							? formatDateTimeEs(documento.fecha_caducidad, { includeTime: false })
							: "",
						Formato: String(documento?.extension || "").toUpperCase(),
						Tipo: documento?.tipo || "",
					}));
				});

			void personasRows;
			void documentosRows;

			const auroraRows = usuariosExportados.map((persona) => {
				const personaDocuments =
					documentsByPersona.get(Number(persona?.id || 0)) || [];
				const documentosExportables = resolveMatchingDocuments(personaDocuments);

				return {
					nombreApellidos: getPersonaDisplayName(persona),
					puedeFichar: boolToSiNoAccent(Number(persona?.activo || 0) === 1),
					empresa: getPersonaCompanyNames(
						persona,
						selectedFolderContext?.campana_nombre || selectedFolderContext?.nombre || ""
					),
					idNacional: persona?.nif || "",
					proteccionDatos: getAuroraDocumentStatus(documentosExportables, [
						"proteccion de datos",
					]),
					dniVerificacion: getAuroraDocumentStatus(documentosExportables, [
						"dni",
						"nie",
						"documentacion",
					]),
					altaSS: getAuroraDocumentStatus(documentosExportables, [
						"alta s.s",
						"alta ss",
						"seguridad social",
						"alta seguridad social",
					]),
				};
			});

			const workbook = await createPersonasDocumentacionExcelWorkbook({
				rows: auroraRows,
				sheetName:
					selectedFolderContext?.nombre ||
					selectedFolderContext?.campana_nombre ||
					(filtroDocumento.trim() ? filtroDocumento.trim() : "AURORA"),
			});

			const excelBuffer = await workbook.xlsx.writeBuffer();
			const blob = new Blob([excelBuffer], {
				type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			});

			saveAs(blob, `personas-documentacion-${hoyYMD}.xlsx`);

			if (
				documentosResult.status !== "fulfilled" ||
				documentosResult.value?.success !== true
			) {
				setSyncMsg(
					"Excel generado. No se pudo completar la hoja de documentos con el indice global."
				);
			}
		} catch (exportError) {
			console.error("Error exportando personas a Excel:", exportError);
			setSyncMsg("Error al generar el Excel solicitado.");
		} finally {
			setExporting(false);
		}
	}, [
		carpetasFiltroById,
		debouncedSearch,
		exporting,
		filtroActivo,
		filtroCarpeta,
		filtroDelegacion,
		filtroCredenciales,
		filtroDocumento,
		filtroEstadoDocumento,
		filtroFoto,
		filtroRol,
		fechaDocumentoDesde,
		fechaDocumentoHasta,
		hasDocumentFilters,
		hoyYMD,
		totalResults,
	]);

	const abrirImagenFichaje = (persona, histItem) => {
		if (!persona || !histItem) return;
		const endpoint =
			typeof histItem?.url_imagen_endpoint === "string" &&
				histItem.url_imagen_endpoint.trim()
				? histItem.url_imagen_endpoint.trim()
				: Number(histItem?.id_empleo) &&
					typeof histItem?.url_imagen === "string" &&
					histItem.url_imagen.trim()
					? `https://recon.jcinformatics.com/imagefichaje.php?id_empleo=${Number(
						histItem.id_empleo
					)}`
					: "";

		if (!endpoint) return;

		setFichajePreview({
			personaNombre: getPersonaDisplayName(persona),
			fecha: histItem?.fecha_cuadrilla || "",
			puntuacion: histItem?.puntuacion_comprobacion ?? null,
			endpoint,
		});
	};

	const cerrarImagenFichaje = () => {
		setFichajePreview(null);
	};

	const cargarDocumentosPersona = async (persona) => {
		if (!persona?.id) {
			return {
				success: true,
				data: { documentos: [] },
				message: "La persona seleccionada no tiene identificador valido.",
			};
		}

		return getDocumentosPersona(persona.id);
	};

	const subirDocumentosPersonaDesdePanel = async (persona, files = [], options = {}) => {
		if (!persona?.id) {
			return {
				success: false,
				message: "La persona seleccionada no tiene identificador valido.",
			};
		}

		const response = await subirDocumentosPersona(persona.id, files, options);

		if (response?.success === true) {
			await refrescarResumenDocumental();
		}

		return response;
	};

	const descargarDocumentoPersonaDesdePanel = async (documento) => {
		if (!documento?.id) {
			throw new Error("Documento sin identificador valido.");
		}

		const { blob, fileName } = await descargarDocumentoPersona(documento.id);
		saveAs(blob, fileName || getPersonaDocumentFallbackName(documento));
	};

	const eliminarDocumentoPersonaDesdePanel = async (documento) => {
		if (!documento?.id) {
			return {
				success: false,
				message: "Documento sin identificador valido.",
			};
		}

		const response = await eliminarDocumentoPersona(documento.id);

		if (response?.success === true) {
			await refrescarResumenDocumental();
		}

		return response;
	};

	const getFichajeImageEndpoint = (histItem) => {
		if (
			typeof histItem?.url_imagen_endpoint === "string" &&
			histItem.url_imagen_endpoint.trim()
		) {
			return histItem.url_imagen_endpoint.trim();
		}

		const idEmpleo = Number(histItem?.id_empleo);
		const urlImagen =
			typeof histItem?.url_imagen === "string" ? histItem.url_imagen.trim() : "";

		if (!idEmpleo || !urlImagen) return "";

		return `https://recon.jcinformatics.com/imagefichaje.php?id_empleo=${idEmpleo}`;
	};

	const handleSincronizar = async (payload) => {
		setSyncing(true);
		setSyncMsg("");

		try {
			const res = await sincronizar(payload);

			if (res?.success) {
				const resumenSync =
					res?.summary?.resumen || res?.resumen || "";
				setSyncMsg(
					resumenSync
						? `Sincronizacion completada: ${resumenSync}`
						: "Sincronizacion completada correctamente."
				);

				await cargarContextoPersonas();
			} else {
				setSyncMsg(res?.message || "Error al sincronizar");
			}
		} catch (e) {
			console.error(e);
			setSyncMsg("Error de red al sincronizar");
		} finally {
			setSyncing(false);
		}
	};

	const submitSync = async () => {
		if (syncMode === "rango") {
			if (!desde || !hasta) {
				setSyncMsg("Selecciona desde y hasta");
				return;
			}

			if (desde > hasta) {
				setSyncMsg("'Desde' no puede ser mayor que 'Hasta'");
				return;
			}

			setOpenSyncDialog(false);
			await handleSincronizar({ desde, hasta });
			return;
		}

		setOpenSyncDialog(false);
		await handleSincronizar({ fecha: hoyYMD });
	};

	const abrirDetallePersona = (persona, event) => {
		if (!persona?.id) return;

		const openInBrowserTab =
			Boolean(event?.metaKey) ||
			Boolean(event?.ctrlKey) ||
			Boolean(event?.shiftKey) ||
			Boolean(event?.altKey) ||
			event?.button === 1 ||
			event?.nativeEvent?.button === 1;

		if (openInBrowserTab) {
			return;
		}

		event?.preventDefault?.();

		navigate(`/personas/${persona.id}`, {
			state: {
				persona,
				from: "/personas",
			},
		});
	};

	const abrirDialogoCredenciales = (persona) => {
		if (!persona?.id) {
			return;
		}

		const suggestedCredentials = buildCredentialSuggestions(persona);

		setCredentialsDialogPersona(persona);
		setCredentialsEnabled(true);
		setCredentialsMail(suggestedCredentials.mail);
		setCredentialsPhone(typeof persona?.telefono === "string" ? persona.telefono : "");
		setCredentialsPassword(suggestedCredentials.password);
		setCredentialsPasswordConfirm(suggestedCredentials.password);
		setCredentialsPasswordTouched(false);
		setCredentialsDialogError("");
		setCredentialsSaving(false);
	};

	const cerrarDialogoCredenciales = () => {
		if (credentialsSaving) {
			return;
		}

		setCredentialsDialogPersona(null);
		setCredentialsMail("");
		setCredentialsPhone("");
		setCredentialsPassword("");
		setCredentialsPasswordConfirm("");
		setCredentialsPasswordTouched(false);
		setCredentialsDialogError("");
	};

	const guardarCredencialesUsuario = async ({ openWhatsapp = false } = {}) => {
		if (!credentialsDialogPersona?.id) {
			setCredentialsDialogError("La persona seleccionada no es valida.");
			return;
		}

		const normalizedMail = credentialsMail.trim();
		const normalizedPhone = normalizeWhatsappPhone(credentialsPhone);
		const shouldPersistPassword =
			credentialsEnabled &&
			(!hasPasswordConfigured(credentialsDialogPersona) ||
				credentialsPasswordTouched ||
				openWhatsapp);
		const necesitaMail = credentialsEnabled && normalizedMail === "";

		if (necesitaMail) {
			setCredentialsDialogError("Debes indicar un email para activar las credenciales.");
			return;
		}

		if (normalizedMail !== "" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedMail)) {
			setCredentialsDialogError("El email indicado no es valido.");
			return;
		}

		if (shouldPersistPassword && credentialsPassword.trim() === "") {
			setCredentialsDialogError("Debes indicar una contraseña para activar las credenciales.");
			return;
		}

		if (shouldPersistPassword || credentialsPasswordTouched) {
			if (credentialsPassword !== credentialsPasswordConfirm) {
				setCredentialsDialogError("Las contraseñas no coinciden.");
				return;
			}

			if (credentialsPassword.trim().length < 4) {
				setCredentialsDialogError("La contraseña debe tener al menos 4 caracteres.");
				return;
			}
		}

		if (openWhatsapp) {
			if (!credentialsEnabled) {
				setCredentialsDialogError("Activa las credenciales antes de enviar el mensaje por WhatsApp.");
				return;
			}

			if (normalizedPhone.length < 10) {
				setCredentialsDialogError("Debes indicar un telefono valido para abrir WhatsApp.");
				return;
			}
		}

		setCredentialsSaving(true);
		setCredentialsDialogError("");

		try {
			const passwordToPersist = shouldPersistPassword ? credentialsPassword.trim() : "";
			const response = await actualizarCredencialesUsuario({
				id_usuario: credentialsDialogPersona.id,
				credenciales_activos: credentialsEnabled ? 1 : 0,
				mail: normalizedMail,
				contrasena: passwordToPersist,
			});

			if (response?.success !== true) {
				setCredentialsDialogError(
					response?.message || response?.error || "No se pudieron actualizar las credenciales."
				);
				return;
			}

			setPersonas((currentPersonas) =>
				currentPersonas.map((persona) =>
					persona.id === credentialsDialogPersona.id
						? {
								...persona,
								credenciales_activos:
									Number(response?.data?.credenciales_activos ?? (credentialsEnabled ? 1 : 0)),
								mail:
									typeof response?.data?.mail === "string"
										? response.data.mail
										: normalizedMail,
								telefono: credentialsPhone,
								rol:
									Number(response?.data?.rol ?? persona?.rol ?? 0) || persona?.rol,
								nombre_rol:
									typeof response?.data?.nombre_rol === "string" &&
									response.data.nombre_rol.trim() !== ""
										? response.data.nombre_rol
										: persona?.nombre_rol,
								tiene_contrasena:
									hasPasswordConfigured(response?.data) ||
									hasPasswordConfigured(persona) ||
									passwordToPersist !== "",
						  }
						: persona
				)
			);

			if (openWhatsapp) {
				const whatsappMessage = buildWhatsappCredentialsMessage({
					persona: credentialsDialogPersona,
					mail: normalizedMail,
					password: passwordToPersist,
				});
				const whatsappUrl = `https://wa.me/${normalizedPhone}?text=${encodeURIComponent(whatsappMessage)}`;
				window.open(whatsappUrl, "_blank", "noopener,noreferrer");
			}

			cerrarDialogoCredenciales();
		} catch (saveError) {
			console.error("Error actualizando credenciales del usuario:", saveError);
			setCredentialsDialogError(
				saveError?.response?.data?.message ||
					saveError?.message ||
					"No se pudieron actualizar las credenciales."
			);
		} finally {
			setCredentialsSaving(false);
		}
	};

	return (
		<Grid container direction="column" size={{ xs: 12 }}>
			<Grid item size={{ xs: 12 }}>
				<SubHeader titulo="Personas" />
			</Grid>

			<Grid container spacing={2} sx={{ px: 2, pb: 2 }} alignItems="center">
				<Button variant="text" onClick={() => setOpenSyncDialog(true)} disabled={syncing}>
					<CloudSyncIcon
						sx={{
							opacity: 0.7,
							animation: syncing ? "spin 1s linear infinite" : "none",
						}}
					/>
					&nbsp;{syncing ? "Sincronizando..." : "Sincronizar"}
				</Button>

				<Button
					variant="text"
					component={RouterLink}
					to="/carpetas"
					sx={{
						fontWeight: isActive("/carpetas") ? 600 : 400,
						borderBottom: isActive("/carpetas")
							? "2px solid"
							: "2px solid transparent",
						borderRadius: 0,
					}}
				>
					Carpetas
				</Button>

				<Button
					variant="text"
					component={RouterLink}
					to="/documentacion"
					sx={{
						fontWeight: isActive("/documentacion") ? 600 : 400,
						borderBottom: isActive("/documentacion")
							? "2px solid"
							: "2px solid transparent",
						borderRadius: 0,
					}}
				>
					Documentos
				</Button>
			</Grid>

			<Grid
				container
				justifyContent="space-between"
				alignItems="center"
				sx={{ px: 3, pb: 1.5 }}
				gap={1.5}
			>
				<Grid container spacing={1.5} size={{ xs: 12, lg: "auto" }} alignItems="center">
					<TextField
						size="small"
						label="Persona"
						placeholder="Nombre, NIF o email..."
						value={busqueda}
						onChange={(e) => {
							resetListadoPage();
							setBusqueda(e.target.value);
						}}
						sx={{ minWidth: { xs: "100%", md: 260 } }}
					/>
					<FormControl size="small" sx={{ minWidth: 140 }}>
						<InputLabel id="filtro-foto-label">Foto</InputLabel>
						<Select
							labelId="filtro-foto-label"
							label="Foto"
							value={filtroFoto}
							onChange={(e) => {
								resetListadoPage();
								setFiltroFoto(e.target.value);
							}}
						>
							<MenuItem value="todas">Todas</MenuItem>
							<MenuItem value="con">Con foto</MenuItem>
							<MenuItem value="sin">Sin foto</MenuItem>
						</Select>
					</FormControl>
					<FormControl size="small" sx={{ minWidth: 180 }}>
						<InputLabel id="filtro-rol-label">Rol</InputLabel>
						<Select
							labelId="filtro-rol-label"
							label="Rol"
							value={filtroRol}
							onChange={(e) => {
								resetListadoPage();
								setFiltroRol(e.target.value);
							}}
						>
							<MenuItem value="">Todos</MenuItem>
							{rolesDisponibles.map((rol) => (
								<MenuItem key={`persona-role-filter-${rol.id}`} value={String(rol.id)}>
									{rol.nombre}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<FormControl size="small" sx={{ minWidth: 150 }}>
						<InputLabel id="filtro-activo-label">Activo</InputLabel>
						<Select
							labelId="filtro-activo-label"
							label="Activo"
							value={filtroActivo}
							onChange={(e) => {
								resetListadoPage();
								setFiltroActivo(e.target.value);
							}}
						>
							<MenuItem value="todos">Todos</MenuItem>
							<MenuItem value="activos">Activos</MenuItem>
							<MenuItem value="no_activos">No activos</MenuItem>
						</Select>
					</FormControl>
					<FormControl size="small" sx={{ minWidth: 170 }}>
						<InputLabel id="filtro-credenciales-label">Credenciales</InputLabel>
						<Select
							labelId="filtro-credenciales-label"
							label="Credenciales"
							value={filtroCredenciales}
							onChange={(e) => {
								resetListadoPage();
								setFiltroCredenciales(e.target.value);
							}}
						>
							<MenuItem value="todos">Todas</MenuItem>
							<MenuItem value="si">Activas</MenuItem>
							<MenuItem value="no">No activas</MenuItem>
						</Select>
					</FormControl>

					<Button
						variant={hasDocumentFilters || openFiltrosDocumentales ? "contained" : "outlined"}
						startIcon={<FilterAltIcon />}
						onClick={() =>
							setOpenFiltrosDocumentales((current) => !current)
						}
					>
						{activeDocumentFiltersCount > 0
							? `Filtros (${activeDocumentFiltersCount})`
							: "Filtros"}
					</Button>

					<Button
						variant="outlined"
						startIcon={<FileDownloadIcon />}
						onClick={exportarPersonasFiltradas}
						disabled={!loaded || exporting || totalResults === 0}
					>
						{exporting ? "Exportando..." : "Exportar"}
					</Button>
				</Grid>

				<Grid size={{ xs: 12, lg: "auto" }}>
					<Typography variant="body2" color="text.secondary">
						{totalResults} persona(s) encontradas
					</Typography>
				</Grid>
			</Grid>

			<Collapse in={openFiltrosDocumentales}>
				<Grid container spacing={1.5} sx={{ px: 3, pb: 2 }} alignItems="center">
					<TextField
						size="small"
						label="Documento"
						placeholder="Nombre o archivo..."
						value={filtroDocumento}
						onChange={(event) => {
							resetListadoPage();
							setFiltroDocumento(event.target.value);
						}}
						sx={{ minWidth: { xs: "100%", md: 240 } }}
					/>

					<FormControl size="small" sx={{ minWidth: 180 }}>
						<InputLabel id="filtro-carpeta-label" shrink>
							Carpeta
						</InputLabel>
						<Select
							labelId="filtro-carpeta-label"
							label="Carpeta"
							multiple
							displayEmpty
							notched
							value={filtroCarpeta}
							onChange={(event) => {
								resetListadoPage();
								setFiltroCarpeta(
									typeof event.target.value === "string"
										? event.target.value.split(",").filter(Boolean)
										: event.target.value
								);
							}}
							renderValue={(selected) =>
								selected.length === 0
									? <Typography component="span" color="text.secondary">Todas</Typography>
									: selected
											.map((folderId) => carpetasFiltroById.get(Number(folderId))?.etiqueta)
											.filter(Boolean)
											.join(", ")
							}
						>
							{carpetasFiltroDisponibles.map((folder) => (
								<MenuItem key={`folder-filter-${folder.id}`} value={String(folder.id)}>
									<Checkbox checked={filtroCarpeta.includes(String(folder.id))} />
									<ListItemText primary={folder.etiqueta} />
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl size="small" sx={{ minWidth: 190 }}>
						<InputLabel id="filtro-delegacion-label" shrink>
							Delegacion
						</InputLabel>
						<Select
							labelId="filtro-delegacion-label"
							label="Delegacion"
							multiple
							displayEmpty
							notched
							value={filtroDelegacion}
							onChange={(event) => {
								resetListadoPage();
								setFiltroDelegacion(
									typeof event.target.value === "string"
										? event.target.value.split(",").filter(Boolean)
										: event.target.value
								);
							}}
							renderValue={(selected) =>
								selected.length === 0
									? <Typography component="span" color="text.secondary">Todas</Typography>
									: selected
											.map(
												(delegacionId) =>
													delegacionesDisponibles.find(
														(delegacion) => String(delegacion.id) === String(delegacionId)
													)?.nombre
											)
											.filter(Boolean)
											.join(", ")
							}
						>
							{delegacionesDisponibles.map((delegacion) => (
								<MenuItem
									key={`delegacion-filter-${delegacion.id}`}
									value={String(delegacion.id)}
								>
									<Checkbox
										checked={filtroDelegacion.includes(String(delegacion.id))}
									/>
									<ListItemText primary={delegacion.nombre} />
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl size="small" sx={{ minWidth: 150 }}>
						<InputLabel id="filtro-estado-documento-label">Estado</InputLabel>
						<Select
							labelId="filtro-estado-documento-label"
							label="Estado"
							value={filtroEstadoDocumento}
							onChange={(event) => {
								resetListadoPage();
								setFiltroEstadoDocumento(event.target.value);
							}}
						>
							<MenuItem value="">Todos</MenuItem>
							<MenuItem value="vigente">Vigente</MenuItem>
							<MenuItem value="proximo">Caduca pronto</MenuItem>
							<MenuItem value="pendiente">Pendiente</MenuItem>
							<MenuItem value="caducado">Caducado</MenuItem>
							<MenuItem value="historico">Historico</MenuItem>
						</Select>
					</FormControl>

					<TextField
						size="small"
						type="date"
						label="Fecha desde"
						value={fechaDocumentoDesde}
						onChange={(event) => {
							resetListadoPage();
							setFechaDocumentoDesde(event.target.value);
						}}
						InputLabelProps={{ shrink: true }}
					/>

					<TextField
						size="small"
						type="date"
						label="Fecha hasta"
						value={fechaDocumentoHasta}
						onChange={(event) => {
							resetListadoPage();
							setFechaDocumentoHasta(event.target.value);
						}}
						InputLabelProps={{ shrink: true }}
					/>

					<Button
						size="small"
						variant="text"
						onClick={limpiarFiltrosDocumentales}
						disabled={!hasDocumentFilters}
					>
						Limpiar
					</Button>

					<Grid size={{ xs: 12 }}>
						<Typography variant="caption" color="text.secondary">
							La fecha cruza la subida del archivo y su caducidad cuando exista.
						</Typography>
					</Grid>
				</Grid>
			</Collapse>

			{syncMsg && (
				<Grid sx={{ px: 3, py: 1 }}>
					<Typography
						variant="body2"
						color={syncMsg.toLowerCase().includes("error") ? "error" : "success.main"}
					>
						{syncMsg}
					</Typography>
				</Grid>
			)}

			{!loaded && (
				<Grid sx={{ px: 3, py: 2 }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<CircularProgress size={18} />
						<Typography variant="body2">Cargando usuarios...</Typography>
					</Box>
				</Grid>
			)}

			{loaded && error && (
				<Grid sx={{ px: 3, py: 2 }}>
					<Typography variant="body2" color="error">
						{error}
					</Typography>
				</Grid>
			)}

			{loaded && !error && documentacionWarning && (
				<Grid sx={{ px: 3, py: 1 }}>
					<Alert severity="warning">{documentacionWarning}</Alert>
				</Grid>
			)}

			<Grid sx={{ width: "100%", overflowX: { xs: "auto", md: "visible" } }}>
				<Grid container direction="column" sx={{ minWidth: 1220 }}>
					<Grid
						container
						alignItems="center"
						sx={{
							px: 3,
							py: 1,
							bgcolor: "grey.100",
							borderBottom: "1px solid",
							borderColor: "divider",
						}}
					>
						<Grid item size={{ xs: 0.4 }} />
						<Grid item size={{ xs: 1.1 }}>Documentos</Grid>
						<Grid item size={{ xs: 2.4 }}>Nombre</Grid>
						<Grid item size={{ xs: 1.1 }}>NIF</Grid>
						{/* <Grid item size={{ xs: 1.6 }}>Departamento</Grid> */}
						<Grid item size={{ xs: 1.1 }}>Rol</Grid>
						<Grid item size={{ xs: 1.1 }}>Activo</Grid>
						<Grid item size={{ xs: 1.2 }}>Credenciales</Grid>
						<Grid item size={{ xs: 1.5 }}>Email</Grid>
					</Grid>
					{loaded &&
						!error &&
						personas.map((persona) => (
							<PersonaCard
								key={`card-${persona.id}`}
								persona={persona}
								isOpen={openRow === persona.id}
								onToggle={() => setOpenRow(openRow === persona.id ? null : persona.id)}
								onAvatarUploaded={handleAvatarUploaded}
								onOpenDetail={abrirDetallePersona}
								onOpenFichajeImage={abrirImagenFichaje}
								onOpenCredentials={abrirDialogoCredenciales}
								loadDocuments={cargarDocumentosPersona}
								uploadDocuments={subirDocumentosPersonaDesdePanel}
								downloadDocument={descargarDocumentoPersonaDesdePanel}
								deleteDocument={eliminarDocumentoPersonaDesdePanel}
							/>
						))}

					{false && loaded && !error && personas.map((u) => {
						const historial = Array.isArray(u.historial_fichajes) ? u.historial_fichajes : [];

						return (
							<React.Fragment key={u.id}>
								<Grid
									container
									alignItems="center"
									sx={{
										px: 3,
										py: 1,
										borderBottom: "1px solid",
										borderColor: "divider",
										"&:hover": { bgcolor: "action.hover" },
									}}
								>
									<Grid item size={{ xs: 0.3 }}>
										<IconButton
											size="small"
											onClick={() => setOpenRow(openRow === u.id ? null : u.id)}
										>
											{openRow === u.id ? <ExpandMoreIcon /> : <ChevronRightIcon />}
										</IconButton>
									</Grid>

									<Grid item size={{ xs: 1.1 }}>
										<Box sx={{ display: "flex", gap: 0.4 }}>
											<InsertDriveFileIcon fontSize="small" color="success" />
											<InsertDriveFileIcon fontSize="small" color="warning" />
											<InsertDriveFileIcon fontSize="small" color="error" />
										</Box>
									</Grid>

									<Grid
										item
										size={{ xs: 2.4 }}
										sx={{ display: "flex", gap: 1, alignItems: "center" }}
									>
										<AvatarPersona
											persona={u}
											size={26}
											allowUpload
											onAvatarUploaded={handleAvatarUploaded}
										/>
										<Typography
											variant="body2"
											noWrap
											onClick={() => abrirDetallePersona(u)}
											sx={{
												cursor: "pointer",
												"&:hover": {
													textDecoration: "underline",
													color: "primary.main",
												},
											}}
										>
											{getPersonaDisplayName(u)}
										</Typography>
									</Grid>

									<Grid item size={{ xs: 1.1 }}>
										<Typography variant="body2" noWrap>{u.nif || "-"}</Typography>
									</Grid>

									<Grid item size={{ xs: 1.6 }}>
										<Typography variant="body2" noWrap>
											{u.nombre_departamento || "-"}
										</Typography>
									</Grid>

									<Grid item size={{ xs: 1.1 }}>
										<Chip size="small" label={u.nombre_rol || "-"} />
									</Grid>
									<Grid item size={{ xs: 1.1 }}>
										<Chip color={u.activo === 1 ? "success" : "error"} size="small" label={u.activo === 1 ? "Sí" : "No"} />
									</Grid>

									<Grid item size={{ xs: 1.8 }}>
										<Typography variant="body2" noWrap>{u.mail || "-"}</Typography>
									</Grid>
								</Grid>

								<Collapse in={openRow === u.id}>
									<Grid
										container
										sx={{
											bgcolor: "grey.50",
											borderBottom: "1px solid",
											borderColor: "divider",
										}}
									>
										<Grid container direction="column" sx={{ px: 3, py: 2, overflowX: "auto" }}>
											<Typography variant="subtitle2" sx={{ mb: 1 }}>
												Historial de fichajes
											</Typography>

											{historial.length === 0 ? (
												<Typography variant="body2" sx={{ opacity: 0.7 }}>
													Sin fichajes recientes.
												</Typography>
											) : (
												<Grid container spacing={1}>
													{historial.map((h) => (
														<Grid
															key={`${h.id_cuadrilla}-${h.fecha_cuadrilla}`}
															item
															size={{ xs: 12 }}
														>
															<Box
																sx={{
																	display: "flex",
																	alignItems: "center",
																	gap: 1,
																	flexWrap: "wrap",
																}}
															>
																<Typography
																	variant="body2"
																	sx={{ minWidth: 140 }}
																>
																	{formatDateTimeEs(h.fecha_cuadrilla)}
																</Typography>
																<Typography
																	variant="body2"
																	sx={{ opacity: 0.8 }}
																>
																	{h.nombre_cliente || "-"}
																</Typography>
																<Chip
																	size="small"
																	label={h.estado_label || "-"}
																	color={getEstadoColor(h.estado)}
																/>
																{h?.puntuacion_comprobacion !== undefined &&
																	h?.puntuacion_comprobacion !== null && (
																		<Chip
																			size="small"
																			variant="outlined"
																			label={formatScoreOverHundred(
																				h.puntuacion_comprobacion
																			)}
																		/>
																	)}
																<Chip
																	size="small"
																	variant="outlined"
																	icon={<PhotoCameraIcon />}
																	label="Foto"
																	disabled={!getFichajeImageEndpoint(h)}
																	clickable={!!getFichajeImageEndpoint(h)}
																	onClick={
																		getFichajeImageEndpoint(h)
																			? () => abrirImagenFichaje(u, h)
																			: undefined
																	}
																	title={
																		getFichajeImageEndpoint(h)
																			? "Ver imagen asociada al fichaje"
																			: "Este fichaje no tiene imagen asociada"
																	}
																/>
															</Box>
														</Grid>
													))}
												</Grid>
											)}
										</Grid>
									</Grid>
								</Collapse>
							</React.Fragment>
						);
					})}

					{loaded && !error && personas.length === 0 && (
						<Grid sx={{ px: 3, py: 2 }}>
							<Typography variant="body2">
								No hay personas que coincidan con los filtros actuales.
							</Typography>
						</Grid>
					)}
				</Grid>
			</Grid>

			<Grid
				container
				justifyContent="space-between"
				alignItems="center"
				sx={{ px: 3, py: 2 }}
				gap={2}
			>
				<FormControl variant="standard" sx={{ minWidth: 120 }}>
					<InputLabel>Filas por pagina</InputLabel>
					<Select
						value={limit}
						onChange={(e) => {
							setLimit(Number(e.target.value));
							setPage(1);
						}}
					>
						<MenuItem value={25}>25</MenuItem>
						<MenuItem value={50}>50</MenuItem>
					</Select>
				</FormControl>

				<Typography variant="body2" color="text.secondary">
					{totalResults} resultado(s)
				</Typography>

				<Pagination
					count={totalPages}
					page={page}
					onChange={(_, p) => {
						setPage(p);
						setOpenRow(null);
					}}
					color="primary"
					disabled={totalResults === 0}
				/>
			</Grid>

			<CredencialesUsuarioDialog
				open={Boolean(credentialsDialogPersona)}
				persona={credentialsDialogPersona}
				credentialsEnabled={credentialsEnabled}
				onCredentialsEnabledChange={setCredentialsEnabled}
				credentialsMail={credentialsMail}
				onCredentialsMailChange={setCredentialsMail}
				credentialsPhone={credentialsPhone}
				onCredentialsPhoneChange={setCredentialsPhone}
				credentialsPassword={credentialsPassword}
				onCredentialsPasswordChange={(value) => {
					setCredentialsPassword(value);
					setCredentialsPasswordTouched(true);
				}}
				credentialsPasswordConfirm={credentialsPasswordConfirm}
				onCredentialsPasswordConfirmChange={(value) => {
					setCredentialsPasswordConfirm(value);
					setCredentialsPasswordTouched(true);
				}}
				credentialsDialogError={credentialsDialogError}
				credentialsSaving={credentialsSaving}
				onClose={cerrarDialogoCredenciales}
				onSave={() => guardarCredencialesUsuario()}
				onSaveAndWhatsapp={() => guardarCredencialesUsuario({ openWhatsapp: true })}
				hasCredencialesActivas={hasCredencialesActivas}
				hasEmailConfigured={hasEmailConfigured}
				hasPasswordConfigured={hasPasswordConfigured}
			/>

			<SincronizarTrabajadoresDialog
				open={openSyncDialog}
				onClose={() => setOpenSyncDialog(false)}
				syncing={syncing}
				syncMode={syncMode}
				onSyncModeChange={setSyncMode}
				desde={desde}
				onDesdeChange={setDesde}
				hasta={hasta}
				onHastaChange={setHasta}
				onSubmit={submitSync}
				hoyYMD={hoyYMD}
			/>

			<FichajePreviewDialog preview={fichajePreview} onClose={cerrarImagenFichaje} />
		</Grid>
	);
};

function getEstadoColor(estado) {
	if (estado === "validado") return "success";
	if (estado === "no_reconocido") return "error";
	return "warning";
}

function useDebouncedValue(value, delay = 300) {
	const [debounced, setDebounced] = useState(value);

	useEffect(() => {
		const t = setTimeout(() => setDebounced(value), delay);
		return () => clearTimeout(t);
	}, [value, delay]);

	return debounced;
}
